import { io } from "socket.io-client";
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Notification from './Notification';
import { useSelector, useDispatch } from 'react-redux';
import { blockUser, unblockUser } from '../redux/blockSlice';
import defaultProfile1 from '../images/default-profile1.jpg';
import { CopyIcon, ScreenIcon } from './icons/Icons';
import styles from './messageBox.module.css';
import ScreenShare from './ScreenShare';
import { Monitor } from 'lucide-react';
import { useScreenShare } from  '../context/ScreenShareContext';
import AudioCallButton from './AudioCallButton';
import { useAuth } from '../context/AuthContext';

const getSocketURL = () => {
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL || 'https://server-production-a1b2.up.railway.app/';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:5000';
};

const socket = io(getSocketURL(), { 
  transports: ['websocket', 'polling'],
  withCredentials: true, // Important for CORS with credentials
  reconnection: true,
  reconnectionAttempts: 5,
  reconnectionDelay: 1000
});

const formatDate = (date) => {
        const d = new Date(date);
        const now = new Date();
        const isThisYear = d.getFullYear() === now.getFullYear();
        const day = d.getDate().toString().padStart(2, '0');
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const year = d.getFullYear().toString().slice(-2);
        const hours = d.getHours().toString().padStart(2, '0');
        const minutes = d.getMinutes().toString().padStart(2, '0');
        return isThisYear ? `${hours}:${minutes} ${day}/${month}` : `${hours}:${minutes} ${day}/${month}/${year}`;
    };

const MessageBox = ({ activeChat, status, userId, onClose }) => {

    const [messages, setMessages] = useState([]);
    const [recentMessage, setRecentMessage] = useState('');
    // const [reloadMessage, setReloadMessage] = useState('');
    const [currentRoomId, setCurrentRoomId] = useState(null);
    const [showOptions, setShowOptions] = useState(null);
    // const [showReadBy, setShowReadBy] = useState(null);
    const [chatBoxPosition, setChatBoxPosition] = useState(() => {
        const savedPosition = localStorage.getItem('chatBoxPosition');
        return savedPosition ? JSON.parse(savedPosition) : {  right: 10, bottom: 20 };
    });
    const bottomRef = useRef(null);
    const chatBoxRef = useRef(null);
    const [showActiveChatOptions, setShowActiveChatOptions] = useState(false);
    const [notification, setNotification] = useState('');
    const blockedStatusState = useSelector(state => 
        state.block.blockedUsers[activeChat._id] || false
    );
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const token = useMemo(() => localStorage.getItem('token'), []);
    const [showScreenShare, setShowScreenShare] = useState(false);
    const { openScreenShare } = useScreenShare();
    // const [totalUnread, setTotalUnread] = useState(0);
    const { openAudioCall } = useScreenShare();
    const { name: userName, profilePicture: userProfilePicture } = useAuth();

    useEffect(() => {
        // console.log("activeChat:", activeChat);
        if (!activeChat?._id) return;

        const startChat = async () => {
            try {
                const response = await axios.post(
                    '/api/applicant/messages/start-chat', 
                    { user1: userId, user2: activeChat?._id },
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (response.data.success) {
                    const roomId = response.data.roomId;
                    setCurrentRoomId(roomId);
                    
                    socket.emit('joinRoom', { roomId });
                    
                    fetchOlderMessages(roomId);
                }
            } catch (error) {
                // console.error('Error starting chat:', error.message);
            }
        };

        startChat();
        return () => {
            if (currentRoomId) {
                socket.emit('leaveRoom', { roomId: currentRoomId });
            }
        };
    }, [activeChat?._id]);
   
    useEffect(() => {
        const handleReceiveMessage = (newMessage) => {
            
            if (newMessage.currentRoomId === currentRoomId) {
                setMessages((prevMessages) => {
                    const exists = prevMessages.some(msg => msg._id === newMessage._id);
                    if (exists) {
                        // console.log('Duplicate message prevented');
                        return prevMessages;
                    }
                    return [...prevMessages, newMessage];
                });
                if (newMessage.senderId !== userId) {
                    setTimeout(() => {
                        axios.post(
                            '/api/applicant/messages/mark-read',
                            { roomId: currentRoomId },
                            { headers: { Authorization: `Bearer ${token}` } }
                        ).catch(err => console.error('Error marking as read:', err));
                    }, 1000);
                }
            }
            //  else {
                // Message from different room - update unread count
                // setTotalUnread(prev => prev + 1);
                // const updateUnreadCount = (prev) => {
                //     return prev.map(friend => {
                //         // Find the friend whose room matches
                //         if (friend.roomId === newMessage.currentRoomId) {
                //             return { 
                //                 ...friend, 
                //                 unreadCount: (friend.unreadCount || 0) + 1 
                //             };
                //         }
                //         return friend;
                //     });
                // };
            
                // setProjectNetworkList(updateUnreadCount);
                // setPreviousNetworkList(updateUnreadCount);
            // }
        };

        socket.on('receiveMessage', handleReceiveMessage);

        return () => {
            socket.off('receiveMessage', handleReceiveMessage);
        };
    }, [currentRoomId, userId]); 

    useEffect(() => {
        const handleMessagesRead = ({ roomId, userId: readByUserId }) => {
            // if (readByUserId === userId) {
            //     const clearUnreadCount = (prev) => {
            //         return prev.map(friend => {
            //             // Find friend by matching their ID with the room participants
            //             // You might need to adjust this based on your data structure
            //             if (friend.roomId === roomId || activeChat?._id === friend._id) {
            //                 return { ...friend, unreadCount: 0 };
            //             }
            //             return friend;
            //         });
            //     };

            //     setProjectNetworkList(clearUnreadCount);
            //     setPreviousNetworkList(clearUnreadCount);
            // }
        };

        socket.on('messagesRead', handleMessagesRead);

        return () => {
            socket.off('messagesRead', handleMessagesRead);
        };
    }, [userId, activeChat]);

    useEffect(() => {
        if (activeChat && currentRoomId && token) {
            // Mark messages as read when opening a chat
            axios.post(
                '/api/applicant/messages/mark-read',
                { roomId: currentRoomId },
                { headers: { Authorization: `Bearer ${token}` } }
            ).catch(err => console.error('Error marking as read on chat open:', err));

            // Clear unread count in UI
            // const clearUnreadForActiveChat = (prev)  => {
            //     return prev.map(friend => 
            //         friend._id === activeChat._id
            //             ? { ...friend, unreadCount: 0 }
            //             : friend
            //     );
            // };

            // setProjectNetworkList(clearUnreadForActiveChat);
            // setPreviousNetworkList(clearUnreadForActiveChat);
        }
    }, [activeChat, currentRoomId, token]);


    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, blockedStatusState]);

    const fetchOlderMessages = useCallback(async (roomId) => {
        // console.log("Fetching old messages for room:", currentRoomId);
        try {
            const response = await axios.post('/api/applicant/messages/get-messages',
                { currentRoomId: roomId },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setMessages(response.data.messages);
            }
        } catch (error) {
            // console.error('Error fetching old messages:', error);
        }
    }, [token]);

    const sendMessage = async () => {

        if (!recentMessage.trim() || !currentRoomId || !userId || !activeChat?._id) {
            // console.error("Missing required fields:", { currentRoomId, userId, activeChat, recentMessage });
            return;
        }
        setRecentMessage('');
        const messageToSend = recentMessage.trim();

        try {
            // const receiverId = activeChat;
            // console.log("currentRoomId:fe3", currentRoomId, "userId:fe3", userId, "activeChat :fe3", activeChat._id, "message:fe3", recentMessage);

            await axios.post('/api/applicant/messages/send-message', {
                roomId: currentRoomId,
                senderId: userId,
                receiverId : activeChat._id,
                message: messageToSend
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

        } catch (error) {
            // console.error("Error sending message:", error.response?.data || error.message);
            setRecentMessage(recentMessage); // Restore on error
        }
    };  

    const handleDeleteMessageForAll = async (messageId) => {
        try {
            await axios.post('/api/applicant/messages/delete-message-for-all', { messageId }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setMessages((prevMessages) => prevMessages.filter(msg => msg._id !== messageId));
        } catch (error) {
            // console.error('Error deleting message for all:', error);
        }
    };

    const handleDeleteMessageForMe = async (messageId) => {
        try {
            await axios.post('/api/applicant/messages/delete-message-for-me', { messageId }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setMessages((prevMessages) => prevMessages.filter(msg => msg._id !== messageId));
        } catch (error) {
            // console.error('Error deleting message for me:', error);
        }
    };

    const handleCopyMessage = (messageId) => {
        const messageToCopy = messages.find(msg => msg._id === messageId);
        if (messageToCopy) {
            navigator.clipboard.writeText(messageToCopy.message)
                .then(() => {
                    setNotification('Message copied to clipboard!');
                    setTimeout(() => setNotification(''), 3000);
                }
                )
                .catch((err) => {
                    // console.error('Could not copy text: ', err);
                    setNotification('Could not copy text:');
                });
        }
    }

    const handleScreenShare = () => {
        openScreenShare({ activeChat, userId, socket });
    };
    const handleAudioCall = () => {
        openAudioCall({ activeChat, userId, socket });
    };


    const handleClickOutside = (event) => {
        if (!event.target.closest(`.${styles.messageOptionsButton}`) && !event.target.closest(`.${styles.messageOptionsMenu}`)) {
            setShowOptions(null);
        }
    };
    const handleClickOutside2 = (event) => {
        if (!event.target.closest(`.${styles.closeButton}`) && !event.target.closest(`.${styles.activeChatOptionsMenu}`)) {
            setShowActiveChatOptions(false);
        }   
    };
        
    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);
    useEffect(() => {
        document.addEventListener('click', handleClickOutside2);
        return () => {
            document.removeEventListener('click', handleClickOutside2);
        };
    }, []);

    const handleDragStart = (e) => {
        const chatBox = chatBoxRef.current;
        const rect = chatBox.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;

        const handleDrag = (e) => {
            const newLeft = e.clientX - offsetX;
            const newTop = e.clientY - offsetY;
            setChatBoxPosition({ top: newTop, left: newLeft });
        };

        const handleDragEnd = () => {
            document.removeEventListener('mousemove', handleDrag);
            document.removeEventListener('mouseup', handleDragEnd);
            localStorage.setItem('chatBoxPosition', JSON.stringify(chatBoxPosition));
        };

        document.addEventListener('mousemove', handleDrag);
        document.addEventListener('mouseup', handleDragEnd);
    };

    // Ya agar parent ka function use karna hai:
    const handleBlock = async () => {
        if (blockedStatusState) {
            await onUnblockUser(activeChat._id);
        } else {
            await onBlockUser(activeChat._id);
        }
        setShowActiveChatOptions(false);
    };

    const onBlockUser = async (networkId) => {
        const result = dispatch(blockUser(networkId));
        if (result.success) {
            setNotification(result.message);
            setTimeout(() => setNotification(''), 3000);
        }
    };

    const onUnblockUser = async (networkId) => {
        const result = dispatch(unblockUser(networkId));
        if (result.success) {
            setNotification(result.message);
            setTimeout(() => setNotification(''), 3000);
        }
    }

    const handleCloseNotification = () => {
        setNotification('');
    };

    const handleProfileClick = (networkId) => {
        navigate(`/profile/${networkId}`, { state: {  status: status  } });  
    };


    return (
        <div>
            {notification && (
                <Notification message={notification} onClose={handleCloseNotification} />
            )}
            <div
                className={styles.chatPopup}
                ref={chatBoxRef}
                style={{ top: chatBoxPosition.top, left: chatBoxPosition.left }}
            >
                <div className={styles.chatPopupHeader} onMouseDown={handleDragStart}>
                    <div className={styles.chatHeaderInfo}>
                        <div className={styles.profileContainer }>
                            <img 
                                title={activeChat.name}
                                src={activeChat.profilePicture ? activeChat.profilePicture.replace('/upload/', '/upload/w_192,h_200,c_fill/') : defaultProfile1}
                                alt={activeChat.name} 
                                className={styles.profilePictureHeader}
                            />
                            <div className={styles.statusDot}></div>
                        </div>
                        <div className={styles.chatInfo}>
                            <h3 className={styles.chatTitle}>{activeChat.name}</h3>
                            <span className={styles.activeStatus}>Active now</span>
                        </div>
                    </div>
                    <AudioCallButton title="Call"
                        receiverId={activeChat._id} 
                        roomId={currentRoomId}
                        receiverName={activeChat.name}
                        receiverProfilePicture={activeChat.profilePicture}
                        callerName={userName}
                        callerProfilePicture={userProfilePicture}
                    />
                    <button title="Share Screen" onClick={()=>{handleScreenShare(activeChat._id)}} className={styles.closeButton}>
                        {/* <ScreenIcon /> */}
                        <Monitor size={16} />
                    </button>
                    <button title="More Options" onClick={()=>{setShowActiveChatOptions(!showActiveChatOptions)}} className={styles.closeButton}>
                        ⋮
                    </button>
                    {showActiveChatOptions && (
                        <div className={styles.activeChatOptionsMenu}>
                            <button onClick={() => {handleProfileClick(activeChat._id)}}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="7" r="4"></circle>
                                    <path d="M5.5 21a8.38 8.38 0 0 1 13 0"></path>
                                </svg>
                                View Profile
                            </button>
                            <button onClick={() => {handleBlock(activeChat._id)}}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                                </svg>
                                {blockedStatusState?"Unblock User":"Block User"}
                            </button>
                        </div>
                    )}  
                    <button title="Close" onClick={onClose} className={styles.closeButton}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>
                <div className={styles.chatBox}>
                    <div className={styles.messagesContainer}>
                        {messages.map((msg, index) => (
                            <div key={index} className={`${styles.message} ${msg.senderId === userId ? styles.sent : styles.received}`}>
                                <div className={styles.messageContent}>
                                    <div className={styles.messageText}>{msg.message}</div>
                                    <button 
                                        className={styles.messageOptionsButton} 
                                        onClick={() => {setShowOptions(showOptions === index ? null : index)}}
                                    >
                                        ⋮
                                    </button>
                                    {showOptions === index && (
                                        <>
                                        <div className={styles.messageOptionsMenu}>
                                            {/* <button onClick={() => {handleMarkRead(msg._id);setShowOptions(null)}}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M9 12l2 2 4-4"/>
                                                    <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"/>
                                                </svg>
                                                Read by
                                            </button> */}
                                            <button onClick={() => {handleCopyMessage(msg._id);setShowOptions(null)}}>
                                                <CopyIcon/>
                                                Copy
                                            </button>
                                            <button onClick={() => {handleDeleteMessageForMe(msg._id);setShowOptions(null);}}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="3,6 5,6 21,6"/>
                                                    <path d="M19,6V20a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6"/>
                                                </svg>
                                                Delete for me
                                            </button>
                                            {msg.senderId === userId && (
                                                <button onClick={() => {handleDeleteMessageForAll(msg._id);setShowOptions(null)}}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <polyline points="3,6 5,6 21,6"/>
                                                        <path d="M19,6V20a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6"/>
                                                    </svg>
                                                    Delete for everyone
                                                </button>
                                            )}
                                        </div>
                                        </>
                                    )}
                                </div>
                                <div className={styles.messageTimestamp}>
                                    {formatDate(msg.createdAt)}
                                </div>
                                {/* {showReadBy === index && msg.readBy && msg.readBy.length > 0 && (
                                    <div className={styles.messageReadRecipients}>
                                        Read by: {msg.readBy.map(recipient => (
                                            <span key={recipient.userId} className={styles.readByUser}>
                                                <img src={recipient.profilePicture} alt={recipient.name} className={styles.recipientProfilePicture} />
                                                {recipient.name}
                                            </span>
                                        ))}
                                    </div>
                                )} */}
                            </div>
                        ))}
                        {blockedStatusState && (
                            <div className={styles.blockedMessage}>
                                You blocked this user. They can't receive messages now.
                            </div>
                        )}
                        <div ref={bottomRef} />
                    </div>
                </div>
                {blockedStatusState===false ? (
                    <div className={styles.inputBox}>
                        <input
                            type="text"
                            value={recentMessage}
                            onChange={(e) => setRecentMessage(e.target.value)}
                            placeholder="Type a message..."
                            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        />
                        <button onClick={sendMessage} className={styles.sendButton} >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="22" y1="2" x2="11" y2="13"></line>
                                <polygon points="22,2 15,22 11,13 2,9 22,2"></polygon>
                            </svg>
                        </button>
                    </div>
                    ) : (
                        <div className={styles.inputBox}>
                        <input
                            type="text"
                            value={recentMessage}
                            onChange={(e) => setRecentMessage(e.target.value)}
                            placeholder="User is Blocked!!"
                            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                            disabled={true}
                        />
                        <button disabled={true} className={styles.sendButton} >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="22" y1="2" x2="11" y2="13"></line>
                                <polygon points="22,2 15,22 11,13 2,9 22,2"></polygon>
                            </svg>
                        </button>
                    </div>
                )}
            </div>
            {showScreenShare && (
                <ScreenShare activeChat={activeChat}
                    userId={userId}
                    // socket={socket}
                    onClose={() => setShowScreenShare(false)}
                />
            )}
        </div>
    );
}
export default MessageBox;